import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TextField,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CoinsWinnersTicker from "./widgets/CoinsWinnersTicker";
import UltimateFan from "./widgets/UltimateFan";
import PromoCarousel from "./widgets/PromoCarousel";
import MiniGameSection from "./widgets/MiniGameSection";
import PrizeDraw from "./widgets/PrizeDraw";
import HallOfFame from "./widgets/HallOfFame";
import AddWidgetButton from "./widgets/AddWidgetButton";
import { useLocation } from "react-router-dom";
import MiniGameSectionVert from "./widgets/MiniGameSectionVert";
import MiniGameSectionBigbox from "./widgets/MiniGameSectionBigbox";
import MiniGameSectionFullbox from "./widgets/MiniGameSectionFullbox";
import MiniGameSectionFeature from "./widgets/MiniGameSectionFeature";
import { get, post, put } from "src/utils/networkUtils";
import AdvertBox from "./widgets/AdvertBox";
import { widgetNames } from "./widgetConstants";
import { getDefaultConfigs } from "./defaultWidgetConfigs";
import WidgetRow from "./WidgetRow";
import WidgetListHeader from "./WidgetListHeader";

function SortableItem({ widget, index, removeWidget, renderWidget }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: widget.widgetId || widget.id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: "8px",
    marginTop: "8px",
  };

  return (
    <Paper ref={setNodeRef} style={style} {...attributes}>
      <WidgetRow
        widget={widget}
        listeners={listeners}
        renderWidget={renderWidget}
        removeWidget={removeWidget}
      />
    </Paper>
  );
}

export default function WidgetManager() {
  const location = useLocation();
  const { state } = location;
  const editData = state?.row;
  const [tags, setTags] = useState([]);
  const [miniGames, setMiniGames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lobbyData, setLobbyData] = useState(
    editData || {
      lobbyName: "",
      lobbyId: Date.now().toString(),
      lobbyStatus: editData?.lobbyStatus ? true : false,
      widgets: [],
    }
  );

  useEffect(() => {
    const handleGetTags = async () => {
      const resp = await get(`/admin/game-providers/tags`);
      setTags(resp?.tags ?? []);
    };
    const handleGetMiniGames = async () => {
      const resp = await get(
        `/admin/game-providers?offset=0&limit=100&isActive=true`
      );
      if (resp?.gameProviders?.length) {
        const data = resp?.gameProviders?.map((gameProvider) => {
          return {
            id: gameProvider.gameProviderId,
            title: gameProvider.gameProviderName,
          };
        });
        setMiniGames(data);
      } else {
        setMiniGames([]);
      }
    };
    handleGetTags();
    handleGetMiniGames();
  }, []);

  const sortedWidgets = React.useMemo(() => {
    return [...lobbyData.widgets].sort(
      (a, b) => a.lobbySectionOrder - b.lobbySectionOrder
    );
  }, [lobbyData.widgets]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = lobbyData.widgets.findIndex(
        (w) => (w?.widgetId || w?.id) === active?.id
      );
      const newIndex = lobbyData.widgets.findIndex(
        (w) => (w.widgetId || w?.id) === over?.id
      );
      if (oldIndex !== -1 && newIndex !== -1) {
        const newWidgets = arrayMove(lobbyData.widgets, oldIndex, newIndex);
        setLobbyData({
          ...lobbyData,
          widgets: newWidgets.map((widget, index) => ({
            ...widget,
            lobbySectionOrder: index + 1,
          })),
        });
      }
    }
  };

  const removeWidget = (id) => {
    const updatedWidgets = lobbyData.widgets
      .filter((widget) => (widget.widgetId || widget.id) !== id)
      .map((widget, index) => ({
        ...widget,
        lobbySectionOrder: index + 1,
      }));

    setLobbyData({
      ...lobbyData,
      widgets: updatedWidgets,
    });
  };

  const updateWidget = (id, config) => {
    setLobbyData({
      ...lobbyData,
      widgets: lobbyData.widgets.map((widget) =>
        (widget.widgetId || widget.id) === id ? { ...widget, config } : widget
      ),
    });
  };

  const renderWidget = (widget) => {
    switch (widget.widgetType) {
      case "coins-winners-ticker":
        return (
          <CoinsWinnersTicker
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
          />
        );
      case "ultimate-fan":
        return (
          <UltimateFan
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
          />
        );
      case "promo-carousel":
        return (
          <PromoCarousel
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
            miniGamesData={miniGames}
          />
        );
      case "mini-game-section-box":
        return (
          <MiniGameSection
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
            tagsData={tags}
          />
        );
      case "mini-game-section-feature":
        return (
          <MiniGameSectionFeature
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
          />
        );
      case "mini-game-section-vert":
        return (
          <MiniGameSectionVert
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
            tagsData={tags}
          />
        );
      case "mini-game-section-big":
        return (
          <MiniGameSectionBigbox
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
            tagsData={tags}
          />
        );
      case "mini-game-section-full":
        return (
          <MiniGameSectionFullbox
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
            tagsData={tags}
          />
        );
      case "prize-draw-active":
        return (
          <PrizeDraw
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
          />
        );
      case "prize-draw-winner":
        return (
          <PrizeDraw
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
          />
        );
      case "hall-of-fame":
        return (
          <HallOfFame
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
          />
        );
      case "advert-box":
        return (
          <AdvertBox
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
            miniGamesData={miniGames}
            dimensionsText={"375x375"}
          />
        );
      case "advert-half-box":
        return (
          <AdvertBox
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
            miniGamesData={miniGames}
            dimensionsText={"375x250"}
          />
        );
      case "advert-banner":
        return (
          <AdvertBox
            config={widget.config}
            onChange={(cfg) => updateWidget(widget.widgetId, cfg)}
            miniGamesData={miniGames}
            dimensionsText={"375x75"}
          />
        );
      default:
        return null;
    }
  };

  const addNewWidget = (widgetType) => {
    const newId = Date.now().toString();
    const newOrder =
      lobbyData.widgets.length > 0
        ? Math.max(...lobbyData.widgets.map((w) => w.lobbySectionOrder)) + 1
        : 1;

    const defaultConfigs = getDefaultConfigs(newId);

    const newWidget = {
      widgetId: newId,
      widgetType,
      name: widgetNames[widgetType],
      lobbySectionOrder: newOrder,
      config: defaultConfigs[widgetType] || {},
    };
    setLobbyData({
      ...lobbyData,
      widgets: [...lobbyData.widgets, newWidget],
    });
  };

  const updateLobbyStatus = (value) => {
    setLobbyData({
      ...lobbyData,
      lobbyStatus: value,
    });
  };
  const updateLobbyName = (value) => {
    setLobbyData({
      ...lobbyData,
      lobbyName: value,
    });
  };
  const handleCreateLobby = async () => {
    try {
      if (editData) {
        setIsLoading(true);
        const resp = await put(`/admin/lobby/${editData.lobbyId}`, lobbyData);
        if (resp.success) {
          setIsLoading(false);
          window.location.href = "/dashboard/lobby";
        }
      } else {
        setIsLoading(true);
        const resp = await post(`/admin/lobby`, lobbyData);
        if (resp.success) {
          setIsLoading(false);
          window.location.href = "/dashboard/lobby";
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.log("error", error);
    }
  };
  return (
    <Box maxWidth="lg" mx="auto">
      {/* Header */}
      <Grid container justifyContent="space-between" alignItems="center" mb={4}>
        <Grid item>
          {editData && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5" fontWeight={600} color="text.primary">
                {lobbyData?.lobbyName}
              </Typography>
              {/* <IconButton size="small">
                <EditOutlinedIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton> */}
            </Stack>
          )}
        </Grid>

        <Grid item>
          <Stack direction="row" spacing={2}>
            <AddWidgetButton onAddWidget={addNewWidget} />
            <Button
              onClick={() => (window.location.href = "/dashboard/lobby")}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateLobby}
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              Save
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Stack
        sx={{
          display: "flex",
          gap: 3,
          flexDirection: "row",
          marginBottom: 2,
          alignItems: "center",
        }}
      >
        <TextField
          fullWidth
          label="Name"
          size="small"
          value={lobbyData.lobbyName}
          onChange={(e) => updateLobbyName(e.target.value)}
        />
        <Stack
          sx={{
            display: "flex",
            gap: 1,
            flexDirection: "row",
            width: "100%",
            alignItems: "center",
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>

            <Select
              value={lobbyData.lobbyStatus}
              label="Status"
              onChange={(e) => updateLobbyStatus(e.target.value)}
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
            </Select>
          </FormControl>
          <Tooltip
            title="Only one lobby can be active at a time. Please deactivate the current lobby before activating another."
            placement="top"
            arrow
          >
            <InfoOutlinedIcon
              fontSize="small"
              color="action"
              sx={{ cursor: "pointer", mt: "4px" }}
            />
          </Tooltip>
        </Stack>
      </Stack>
      <WidgetListHeader />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedWidgets.map((w) => w.widgetId || w.id)}
            strategy={verticalListSortingStrategy}
          >
            {sortedWidgets?.map((widget, index) => (
              <SortableItem
                key={widget.widgetId || widget.id}
                widget={widget}
                index={index}
                removeWidget={removeWidget}
                renderWidget={renderWidget}
              />
            ))}
          </SortableContext>
        </DndContext>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 2,
          }}
        >
          <AddWidgetButton onAddWidget={addNewWidget} />
        </Box>
      </Box>
    </Box>
  );
}
